import React from 'react';
import { Box, TextField, FormControlLabel, Checkbox, Link, Typography } from '@mui/material';
import CustomButton from 'shared/custom-button/CustomButton';
import { colors } from 'app/providers/theme/colors-type';
import styles from 'features/mite-policy/step-2/StepTwo.module.css';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';
import InfoIcon from '@mui/icons-material/Info';
import { IStepTwo } from 'entities/mite-policy/step-2/types/step-two-inerface';
import { useAppDispatch } from 'shared/utils/hooks/useRedux';
import { updateField } from 'entities/mite-policy/step-2';

interface InsuranceTermStepProps {
    formState: IStepTwo;
    errors: Record<string, string>;
    handleChange: (fieldName: keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleCheckboxChange: (fieldName: keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    handleNextStep: () => void;
    handleBack: () => void;
    openDatePicker: boolean;
    setOpenDatePicker: (value: boolean) => void;
}

export const InsuranceTermStep: React.FC<InsuranceTermStepProps> = ({
    formState,
    errors,
    handleChange,
    handleCheckboxChange,
    handleNextStep,
    handleBack,
    openDatePicker,
    setOpenDatePicker,
}) => {
    const localeText = {
        cancelButtonLabel: 'Отменить',
        okButtonLabel: 'ОК',
        toolbarTitle: 'Выберите дату начала',
    };
    const dispatch = useAppDispatch();
    return (
        <Box>
            <Box className={styles.titleBox}>
                <h4>Срок страхования</h4>
            </Box>
            <Box className={styles.colorBox}>
                <span className={styles.icon}><InfoIcon /></span>
                <p>Полис начнёт действовать не ранее, чем через 3 дня после покупки</p>
            </Box>
            <Box className={styles.dateBox}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru} localeText={localeText}>
                    <DatePicker
                        open={openDatePicker}
                        onClose={() => setOpenDatePicker(false)}
                        value={formState.BeginDate ? new Date(formState.BeginDate.split('.').reverse().join('-')) : null}
                        onChange={(newValue) => {
                            if (newValue) {
                                const day = String(newValue.getDate()).padStart(2, '0');
                                const month = String(newValue.getMonth() + 1).padStart(2, '0');
                                const year = newValue.getFullYear();
                                const formattedDate = `${day}.${month}.${year}`;
                                dispatch(updateField({ field: 'BeginDate', value: formattedDate }));
                            } else {
                                dispatch(updateField({ field: 'BeginDate', value: '' }));
                            }
                            setOpenDatePicker(false);
                        }}
                        minDate={new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)}
                        maxDate={new Date(new Date().setMonth(new Date().getMonth() + 3))}
                        enableAccessibleFieldDOMStructure={false}
                        slots={{
                            textField: (params) => (
                                <TextField
                                    {...params}
                                    label="Дата начала"
                                    variant="outlined"
                                    placeholder="дд.мм.гггг"
                                    onClick={() => setOpenDatePicker(true)}
                                    error={!!errors.BeginDate}
                                    helperText={errors.BeginDate}
                                    InputProps={{
                                        ...params.InputProps,
                                        readOnly: true,
                                    }}
                                />
                            ),
                        }}
                        slotProps={{
                            toolbar: {
                                hidden: true,
                            },
                        }}
                    />
                </LocalizationProvider>
                <TextField
                    name="EndDate"
                    label="Дата окончания"
                    variant="outlined"
                    value={formState.EndDate}
                    InputProps={{
                        readOnly: true,
                    }}
                />
            </Box>
            <Box className={styles.checkboxBox}>
                <FormControlLabel
                    control={
                        <Checkbox
                            name="AgreePolitics"
                            checked={Boolean(formState.AgreePolitics)}
                            onChange={handleCheckboxChange('AgreePolitics')}
                            sx={{
                                color: 'grey.500',
                                '&.Mui-checked': {
                                    color: colors.primary,
                                },
                            }}
                        />
                    }
                    label={
                        <>
                            <p>Даю согласие на обработку персональных данных, ознакомлен с{' '}
                                <Link
                                    href="https://hr-dms.ugsk.ru/processing-policy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="always"
                                >
                                    Политикой
                                </Link>{' '}
                                обработки персональных данных</p>
                        </>
                    }
                />
                {errors.AgreePolitics && <Typography color="error">{errors.AgreePolitics}</Typography>}
                <FormControlLabel
                    control={
                        <Checkbox
                            name="AgreeAd"
                            checked={Boolean(formState.AgreeAd)}
                            onChange={handleCheckboxChange('AgreeAd')}
                            sx={{
                                color: 'grey.500',
                                '&.Mui-checked': {
                                    color: colors.primary,
                                },
                            }}
                        />
                    }
                    label={
                        <>
                            <p>Даю {' '}
                                <Link
                                    href="https://hr-dms.ugsk.ru/ConsentAdvertisingMaterials.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="always"
                                >
                                    согласие
                                </Link>{' '}
                                на получение рекламных материалов</p>
                        </>
                    }
                />
            </Box>
            <Box className={styles.btnBoxNext}>
                <CustomButton
                    title="Назад"
                    handleClick={handleBack}
                    textColor={colors.primary}
                    backgroundColor="none"
                    width="150px"
                    gap="10px"
                    borderRadius='4px'
                    padding='3px'
                    border='none'
                />
                <CustomButton
                    title="Далее"
                    handleClick={handleNextStep}
                    textColor="white"
                    backgroundColor={colors.primary}
                    width="150px"
                    gap="10px"
                    borderRadius='4px'
                    padding='3px'
                />
            </Box>
        </Box>
    );
};
